import React, {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { GetRewardsEnrollmentResponse } from '@clearcover/core-app-types';
// Import from concrete client files, not the Shared/services/Api barrel.
// Same cycle constraint as useIncentiveProgramEnrollment — see
// .agents/lessons/shared-hooks-patterns.md (RVL-3151).
import { useLazyIncentiveProgramsApi } from 'Shared/services/Api/makeIncentiveProgramsClient';
import { useLazyRewardsApi } from 'Shared/services/Api/makeRewardsClient';
import {
  IncentiveProgramEnrollmentResponse,
  IncentiveProgramEnrollmentState,
  IncentiveProgramEnrollmentStatus,
} from 'Shared/hooks/useIncentiveProgramEnrollment/useIncentiveProgramEnrollment';
import { useAuth0 } from 'Auth';
import { env } from 'Shared/constants/env';
import { GetRewardsConfigResponse } from 'Rewards/services/endpoints/GetRewardsConfig/types';
import { MOCK_REWARDS_CONFIG } from 'Rewards/services/endpoints/GetRewardsConfig/mockConfig';
import { GetAgentStatusResponse } from 'Rewards/services/endpoints/GetAgentStatus/types';
import { MOCK_AGENT_STATUS } from 'Rewards/services/endpoints/GetAgentStatus/mockStatus';
import { PostAttemptResponse } from 'Rewards/services/endpoints/PostAttempt/types';
import { RewardsSource, RewardsEnrollmentContextValue } from './types';

const TEN_SECONDS = 10000;

const isEnrolledIn26For26 = (
  status: IncentiveProgramEnrollmentStatus
): boolean => status === 'TC_PENDING' || status === 'ACTIVE';

const MOCK_INCENTIVE_RESPONSE: IncentiveProgramEnrollmentState = {
  isEnrolled: true,
  status: 'ACTIVE',
};
const MOCK_REWARDS_RESPONSE: GetRewardsEnrollmentResponse = {
  isEnrolled: true,
};

const DEFAULT_VALUE: RewardsEnrollmentContextValue = {
  source: 'none',
  loading: true,
  incentiveProgramEnrollmentResponse: null,
  rewardsEnrollmentResponse: null,
  refresh: async () => {},
  config: null,
  status: null,
  pendingAttempt: null,
  rewardsDataLoading: false,
  rewardsDataError: false,
  loadRewardsData: async () => {},
  refetchStatus: async () => null,
  setPendingAttempt: () => {},
};

// Exported for tests — wrap your render with `<RewardsEnrollmentContext.Provider value={...}>`
// to supply a default-resolved context (loading=false, source='none') so Core doesn't render
// LoadingPage indefinitely. App code should use `useRewardsEnrollmentContext` instead.
export const RewardsEnrollmentContext = createContext<
  RewardsEnrollmentContextValue
>(DEFAULT_VALUE);

export const useRewardsEnrollmentContext = (): RewardsEnrollmentContextValue =>
  useContext(RewardsEnrollmentContext);

export const RewardsEnrollmentProvider: FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth0();
  const [source, setSource] = useState<RewardsSource>('none');
  const [loading, setLoading] = useState<boolean>(true);
  const [incentiveResponse, setIncentiveResponse] = useState<
    IncentiveProgramEnrollmentState
  >(null);
  const [
    rewardsResponse,
    setRewardsResponse,
  ] = useState<GetRewardsEnrollmentResponse | null>(null);
  const sourceCommittedRef = useRef(false);
  const resolveStartedRef = useRef(false);

  const [
    ,
    executeIncentiveFetch,
    incentiveProgramsToken,
  ] = useLazyIncentiveProgramsApi<IncentiveProgramEnrollmentResponse>({});
  const [, executeRewardsFetch] = useLazyRewardsApi<
    GetRewardsEnrollmentResponse
  >({});

  // ── Lazy 26for26 spin data — populated only when a rewards screen calls
  // `loadRewardsData()`. Stays null on /quotes, /policies, /admin so the
  // app-root provider does not add API noise outside the rewards surface.
  const [config, setConfig] = useState<GetRewardsConfigResponse | null>(null);
  const [status, setStatus] = useState<GetAgentStatusResponse | null>(null);
  const [
    pendingAttempt,
    setPendingAttempt,
  ] = useState<PostAttemptResponse | null>(null);
  const [rewardsDataLoading, setRewardsDataLoading] = useState<boolean>(false);
  const [rewardsDataError, setRewardsDataError] = useState<boolean>(false);
  const rewardsDataLoadedRef = useRef(false);
  const rewardsDataPromiseRef = useRef<Promise<void> | null>(null);

  const [, executeConfigFetch] = useLazyIncentiveProgramsApi<
    GetRewardsConfigResponse
  >({});
  const [, executeStatusFetch] = useLazyIncentiveProgramsApi<
    GetAgentStatusResponse
  >({});

  // Sticky decision: once committed, source never changes for the session.
  const commitSource = useCallback((next: RewardsSource) => {
    if (sourceCommittedRef.current) return;
    sourceCommittedRef.current = true;
    setSource(next);
  }, []);

  useEffect(() => {
    if (!user?.id || !incentiveProgramsToken) return;
    if (resolveStartedRef.current) return;
    resolveStartedRef.current = true;

    // Mock short-circuit — render the 26for26 surface, no API calls.
    if (env.rewardsMockEnabled) {
      setIncentiveResponse(MOCK_INCENTIVE_RESPONSE);
      setRewardsResponse(MOCK_REWARDS_RESPONSE);
      commitSource('26for26');
      setLoading(false);
      return;
    }

    let cancelled = false;
    const resolve = async () => {
      // Step 1 — prime, only if hostname is configured for this tier.
      if (env.incentiveProgramsHostname) {
        try {
          const response = await executeIncentiveFetch({
            url: `/agents/${user.id}/enrollment`,
            timeout: TEN_SECONDS,
          });
          if (cancelled) return;
          const data = response.data;
          if (data) {
            const enrolled = isEnrolledIn26For26(data.status);
            setIncentiveResponse({ isEnrolled: enrolled, status: data.status });
            if (enrolled) {
              commitSource('26for26');
              setLoading(false);
              return;
            }
            // 2xx with non-enrolled status → fall through to legacy
          }
        } catch {
          // Non-2xx / network / timeout — per HITL: hide rewards entirely.
          if (cancelled) return;
          commitSource('none');
          setLoading(false);
          return;
        }
      }

      // Step 2 — legacy fallback
      try {
        const response = await executeRewardsFetch({
          url: `/v1/enrollment`,
          timeout: TEN_SECONDS,
        });
        if (cancelled) return;
        const data = response.data;
        if (data) {
          setRewardsResponse(data);
          commitSource(data.isEnrolled ? 'legacy' : 'none');
        } else {
          commitSource('none');
        }
      } catch {
        if (cancelled) return;
        commitSource('none');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    resolve();
    return () => {
      cancelled = true;
      // Reset the start guard so a re-render with new fetch refs (e.g.
      // Auth0's silent JWT refresh changes `executeIncentiveFetch`) can
      // re-resolve. Without this, the app stays stuck on `loading=true`
      // because the effect early-returns before calling `setLoading(false)`.
      // `sourceCommittedRef` is intentionally NOT reset — once committed,
      // the surface decision is sticky for the session.
      resolveStartedRef.current = false;
    };
  }, [
    user?.id,
    executeIncentiveFetch,
    executeRewardsFetch,
    commitSource,
    incentiveProgramsToken,
  ]);

  useEffect(() => {
    if (!incentiveResponse) return;
    window.FS?.setVars('user', {
      enrolled_in_26_for_26: isEnrolledIn26For26(incentiveResponse.status),
    });
  }, [incentiveResponse]);

  const refresh = useCallback(async () => {
    if (!user?.id || env.rewardsMockEnabled) return;
    // Refresh re-fetches the underlying data so consumers see updated
    // status (e.g. after acceptTerms flips TC_PENDING → ACTIVE). It never
    // changes the committed source — the user stays on whichever surface
    // was decided at first resolution. Errors are re-thrown so callers
    // (e.g. T&C accept handler) can surface a retry/error UI rather than
    // leaving the user silently stranded on a stale screen.
    if (env.incentiveProgramsHostname) {
      const response = await executeIncentiveFetch({
        url: `/agents/${user.id}/enrollment`,
        timeout: TEN_SECONDS,
      });
      const data = response.data;
      if (data) {
        setIncentiveResponse({
          isEnrolled: isEnrolledIn26For26(data.status),
          status: data.status,
        });
      }
    }
    if (source === 'legacy') {
      const response = await executeRewardsFetch({
        url: `/v1/enrollment`,
        timeout: TEN_SECONDS,
      });
      const data = response.data;
      if (data) setRewardsResponse(data);
    }
  }, [user?.id, source, executeIncentiveFetch, executeRewardsFetch]);

  // ── Imperative loader for /config/current + /status. Idempotent and
  // promise-coalesced so concurrent rewards-screen mounts trigger a single
  // fetch pair. Gated on the 26for26 surface — legacy/none agents never
  // hit these endpoints. NEVER invoked from a provider effect; the rewards
  // screens call it from their own useEffect on mount.
  const loadRewardsData = useCallback(async () => {
    if (rewardsDataLoadedRef.current) return;
    if (rewardsDataPromiseRef.current) return rewardsDataPromiseRef.current;
    if (!user?.id) return;

    if (env.rewardsMockEnabled) {
      setConfig(MOCK_REWARDS_CONFIG);
      setStatus(MOCK_AGENT_STATUS);
      rewardsDataLoadedRef.current = true;
      return;
    }

    // Early-return is intentional. On a hard-refresh of /rewards/spin the
    // SpinWheel mounts and calls this loader before the enrollment fetch
    // settles `source` to '26for26'. When source resolves later, this
    // useCallback's identity changes (source is a dep), which re-fires the
    // consumer effects in useSpinWheel and TwentySixForTwentySix and the
    // load runs successfully. Do NOT move this guard above the
    // `rewardsDataLoadedRef`/`rewardsDataPromiseRef` checks above, or
    // concurrent callers will redundantly flip loading state.
    if (!incentiveProgramsToken || source !== '26for26') return;

    setRewardsDataLoading(true);
    setRewardsDataError(false);
    const promise = (async () => {
      try {
        const [configRes, statusRes] = await Promise.all([
          executeConfigFetch({
            url: `/config/current`,
            timeout: TEN_SECONDS,
          }),
          executeStatusFetch({
            url: `/agents/${user.id}/status`,
            timeout: TEN_SECONDS,
          }),
        ]);
        // Treat empty/null bodies as a load failure rather than silently
        // committing the loaded ref. Otherwise consumers see config=null
        // forever with no error flag and no way to retry — `loadRewardsData`
        // would no-op on every subsequent call.
        if (!configRes.data || !statusRes.data) {
          setRewardsDataError(true);
          return;
        }
        setConfig(configRes.data);
        setStatus(statusRes.data);
        rewardsDataLoadedRef.current = true;
      } catch {
        setRewardsDataError(true);
      } finally {
        setRewardsDataLoading(false);
        rewardsDataPromiseRef.current = null;
      }
    })();
    rewardsDataPromiseRef.current = promise;
    return promise;
  }, [
    user?.id,
    incentiveProgramsToken,
    source,
    executeConfigFetch,
    executeStatusFetch,
  ]);

  const refetchStatus = useCallback(async () => {
    if (!user?.id) return null;
    if (env.rewardsMockEnabled) {
      setStatus(MOCK_AGENT_STATUS);
      return MOCK_AGENT_STATUS;
    }
    try {
      const response = await executeStatusFetch({
        url: `/agents/${user.id}/status`,
        timeout: TEN_SECONDS,
      });
      if (response.data) {
        setStatus(response.data);
        return response.data;
      }
      return null;
    } catch {
      return null;
    }
  }, [user?.id, executeStatusFetch]);

  return (
    <RewardsEnrollmentContext.Provider
      value={{
        source,
        loading,
        incentiveProgramEnrollmentResponse: incentiveResponse,
        rewardsEnrollmentResponse: rewardsResponse,
        refresh,
        config,
        status,
        pendingAttempt,
        rewardsDataLoading,
        rewardsDataError,
        loadRewardsData,
        refetchStatus,
        setPendingAttempt,
      }}
    >
      {children}
    </RewardsEnrollmentContext.Provider>
  );
};
